import MailOutlineIcon from '@mui/icons-material/MailOutline';
import {
  Box,
  Button,
  Divider,
  InputAdornment,
  TextField,
  Typography
} from '@mui/material';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import AuthService from '../../../services/auth.service';
import { GhlService } from '../../../services/ghl/ghl.service';
import { SocialProvider } from '../../../types/auth';
import { validateEmail } from '../../../utils/common.util';

const actionButtonSx = {
  justifyContent: 'space-between',
  textTransform: 'none',
  borderRadius: '10px',
  py: 1.05,
  px: 1.4,
  color: '#111827',
  borderColor: 'rgba(17, 24, 39, 0.14)',
  bgcolor: '#FFFFFF',
  '&:hover': {
    borderColor: 'rgba(17, 24, 39, 0.24)',
    bgcolor: '#F9FAFB',
  },
} as const;

interface Step1EmailProps {
  email: string;
  onEmailChange: (email: string) => void;
  onComplete: (email: string, ghlContactId: string) => void;
  onSocialSignIn: (provider: SocialProvider) => void;
}

export default function Step1Email({
  email,
  onEmailChange,
  onComplete,
  onSocialSignIn,
}: Step1EmailProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [emailError, setEmailError] = useState('');
  const processedUrlEmailRef = useRef<string | null>(null);

  const getQueryParamPreservingPlus = useCallback((query: string, key: string) => {
    const normalizedQuery = query.startsWith('?') ? query.slice(1) : query;
    if (!normalizedQuery) return null;
    const matcher = new RegExp(`(?:^|&)${key}=([^&]*)`);
    const match = normalizedQuery.match(matcher);
    if (!match) return null;
    try {
      return decodeURIComponent(match[1]);
    } catch (_error: unknown) {
      return match[1];
    }
  }, []);

  const getEmailFromUrl = useCallback(() => {
    const currentUrl = new URL(window.location.href);
    const emailFromSearch = getQueryParamPreservingPlus(currentUrl.search, 'email');
    if (emailFromSearch) return emailFromSearch;
    const hash = currentUrl.hash || '';
    const queryStartIndex = hash.indexOf('?');
    if (queryStartIndex === -1) return null;
    const hashQuery = hash.slice(queryStartIndex + 1);
    return getQueryParamPreservingPlus(hashQuery, 'email');
  }, [getQueryParamPreservingPlus]);

  const removeEmailFromUrl = useCallback(() => {
    const currentUrl = new URL(window.location.href);
    currentUrl.searchParams.delete('email');

    const hash = currentUrl.hash || '';
    const queryStartIndex = hash.indexOf('?');
    if (queryStartIndex !== -1) {
      const hashPath = hash.slice(0, queryStartIndex);
      const hashQuery = hash.slice(queryStartIndex + 1);
      const hashParams = new URLSearchParams(hashQuery);
      hashParams.delete('email');
      const nextHashQuery = hashParams.toString();
      currentUrl.hash = nextHashQuery ? `${hashPath}?${nextHashQuery}` : hashPath;
    }

    const updatedUrl = `${currentUrl.pathname}${currentUrl.search}${currentUrl.hash}`;
    window.history.replaceState(window.history.state, '', updatedUrl);
  }, []);

  const processEmail = useCallback(
    async (emailToProcess: string, source: 'url' | 'submit') => {
      setIsLoading(true);
      setEmailError('');
      try {
        const result = await AuthService.checkEmailExists(emailToProcess);
        const exists = Boolean(result?.data?.exists);

        if (exists) {
          setEmailError('This email is already registered. Please sign in instead.');
          return;
        }

        const ghlContactId = await GhlService.createContact({ email: emailToProcess, tags: source === 'url' ? ["Url Email"]:['Custom Email'] });
        onComplete(emailToProcess, ghlContactId || '');
        removeEmailFromUrl();
      } catch (_err: unknown) {
        setEmailError('Unable to verify email. Please try again.');
      } finally {
        setIsLoading(false);
      }
    },
    [onComplete]
  );

  useEffect(() => {
    const emailFromParams = getEmailFromUrl();
    console.log("emailFromParams",emailFromParams);
    
    if (!emailFromParams) return;

    const normalizedEmail = emailFromParams.trim();
    if (!validateEmail(normalizedEmail).isValid) return;
    if (processedUrlEmailRef.current === normalizedEmail) return;

    processedUrlEmailRef.current = normalizedEmail;

    if (!email.trim()) onEmailChange(normalizedEmail);
    processEmail(normalizedEmail, 'url');
  }, [email, getEmailFromUrl, onEmailChange, processEmail]);

  const trimmedEmail = useMemo(() => email.trim(), [email]);
  const isContinueDisabled = trimmedEmail.length === 0 || isLoading;

  const handleEmailChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onEmailChange(e.target.value);
      if (emailError) setEmailError('');
    },
    [onEmailChange, emailError]
  );

  const handleSubmit = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      if (!trimmedEmail) return;

      await processEmail(trimmedEmail, 'submit');
    },
    [trimmedEmail, processEmail]
  );

  return (
    <>
      <Typography
        component="h1"
        sx={{
          textAlign: 'center',
          color: '#111827',
          fontSize: { xs: '1.8rem', sm: '2rem', md: '2.2rem' },
          fontWeight: 700,
          letterSpacing: '-0.01em',
          mb: 1,
        }}
      >
        Create your account
      </Typography>
      <Typography
        sx={{
          textAlign: 'center',
          color: '#6B7280',
          fontSize: '0.92rem',
          mb: { xs: '2rem', sm: '2.4rem' },
        }}
      >
        Your webinar funnel is just a step away
      </Typography>

      {/* <Stack spacing={1.55} mb={0}>
        <Button
          variant="outlined"
          onClick={() => onSocialSignIn('Google')}
          sx={{
            ...actionButtonSx,
            '& .arrow-animate': { transition: 'transform 0.18s cubic-bezier(0.43,0.13,0.23,0.96)' },
            '&:hover .arrow-animate': { transform: 'translateX(4px)' },
          }}
        >
          <Stack direction="row" spacing={1.1} alignItems="center">
            <GoogleIcon />
            <Typography sx={{ fontSize: '0.89rem', fontWeight: 500 }}>Continue with Google</Typography>
          </Stack>
          <ArrowForwardIosRoundedIcon className="arrow-animate" sx={{ fontSize: 12, color: '#6B7280', ml: 0.4 }} />
        </Button>

        <Button
          variant="outlined"
          onClick={() => onSocialSignIn('Linkedin')}
          sx={{
            ...actionButtonSx,
            '& .arrow-animate': { transition: 'transform 0.18s cubic-bezier(0.43,0.13,0.23,0.96)' },
            '&:hover .arrow-animate': { transform: 'translateX(4px)' },
          }}
        >
          <Stack direction="row" spacing={1.1} alignItems="center">
            <LinkedInsmallIcon />
            <Typography sx={{ fontSize: '0.89rem', fontWeight: 500 }}>Continue with LinkedIn</Typography>
          </Stack>
          <ArrowForwardIosRoundedIcon className="arrow-animate" sx={{ fontSize: 12, color: '#6B7280', ml: 0.4 }} />
        </Button>
      </Stack> */}

      {/* <Stack direction="row" alignItems="center" spacing={1.5} sx={{ my: 2.5 }}>
        <Divider sx={{ flex: 1, borderColor: 'rgba(17, 24, 39, 0.12)' }} />
        <Typography sx={{ color: '#9CA3AF', fontWeight: 600, fontSize: '0.72rem' }}>OR</Typography>
        <Divider sx={{ flex: 1, borderColor: 'rgba(17, 24, 39, 0.12)' }} />
      </Stack> */}

      <Box component="form" onSubmit={handleSubmit} autoComplete="off">
        <TextField
          disabled={isLoading}
          fullWidth
          value={email}
          onChange={handleEmailChange}
          placeholder="Use email address"
          variant="outlined"
          size="medium"
          error={!!emailError}
          helperText={emailError}
          inputProps={{ autoComplete: 'off' }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <MailOutlineIcon sx={{ fontSize: 17, color: '#9CA3AF' }} />
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: '10px',
              bgcolor: '#FFFFFF',
              '& fieldset': { borderColor: emailError ? undefined : 'rgba(17, 24, 39, 0.14)' },
              '&:hover fieldset': { borderColor: emailError ? undefined : 'rgba(17, 24, 39, 0.22)' },
            },
            '& input': { fontSize: '0.9rem', color: '#111827', py: 1.1 },
            '& input::placeholder': { color: '#9CA3AF', opacity: 1 },
            mb: emailError ? 1 : 2,
          }}
        />

        <Button
          fullWidth
          size='small'
          type="submit"
          variant="contained"
          disabled={isContinueDisabled}
          sx={{
            textTransform: 'none',
            borderRadius: '10px',
            py: 1,
            fontSize: '0.92rem',
            fontWeight: 600,
            color: '#FFFFFF',
            bgcolor: '#4058fb',
            boxShadow: 'none',
            mt: emailError ? 1 : 0,
            '&:hover': { bgcolor: '#3442bd', boxShadow: 'none' },
            '&.Mui-disabled': {
              bgcolor: '#4058fb', 
              color: '#C5C7D0',   
              opacity: 0.6,       
              cursor: 'not-allowed',
              boxShadow: 'none'
            },
          }}
        >
          {isLoading ? 'Checking...' : 'Continue'}
        </Button>
      </Box>

      <Divider sx={{ my: 2.5, borderColor: 'rgba(17, 24, 39, 0.12)' }} />
      <Typography sx={{ textAlign: 'center', color: '#4B5563', fontSize: '0.9rem' }}>
        Already have an account?{' '}
        <Typography
          component="a"
          href="/login"
          sx={{
            display: 'inline',
            color: '#6066E9',
            textDecoration: 'none',
            fontSize: 'inherit',
            '&:hover': { textDecoration: 'underline' },
          }}
        >
          Sign in here
        </Typography>
      </Typography>
    </>
  );
}
